import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** Optional supporting line under the title. */
  description?: React.ReactNode;
  /** Optional leading icon (e.g. a Remix icon node) shown left of the title. */
  icon?: React.ReactNode;
  children?: React.ReactNode;
  /** Footer actions, right-aligned. Omit for a body-only dialog. */
  footer?: React.ReactNode;
  size?: 'sm' | 'md';
  /** Close when clicking the dimmed backdrop. Default true. */
  closeOnBackdrop?: boolean;
  /** Element to focus when the dialog opens (defaults to the first focusable). */
  initialFocusRef?: React.RefObject<HTMLElement>;
}

const WIDTHS: Record<NonNullable<DialogProps['size']>, number> = {
  sm: 400,
  md: 520,
};

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Centered modal primitive — portal to <body>, dimmed backdrop, ESC + backdrop
 * close, focus trap (Tab cycles within the panel), focus restore on close, and
 * body-scroll lock while open. ARIA: role="dialog" + aria-modal, labelled by the
 * title. Footer action buttons should keep ≥44px touch targets (min-h-11).
 */
export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  description,
  icon,
  children,
  footer,
  size = 'sm',
  closeOnBackdrop = true,
  initialFocusRef,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const titleId = React.useId();
  const descId = React.useId();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const nodes = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (nodes.length === 0) {
        e.preventDefault();
        panelRef.current.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown, true);

    // Focus the requested element, else the first focusable, else the panel.
    const toFocus =
      initialFocusRef?.current ??
      panelRef.current?.querySelector<HTMLElement>(FOCUSABLE) ??
      panelRef.current;
    // Defer so the portal node is in the DOM and laid out.
    const raf = requestAnimationFrame(() => toFocus?.focus());

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = prevOverflow;
      restoreFocusRef.current?.focus?.();
    };
  }, [open, handleKeyDown, initialFocusRef]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(14,18,27,0.45)' }}
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className="flex w-full flex-col rounded-2xl bg-bg-white-0 shadow-card-lg outline-none"
        style={{ maxWidth: WIDTHS[size], maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-start gap-3 border-b border-stroke-soft-200 p-5">
          {icon && <div className="flex-shrink-0">{icon}</div>}
          <div className="min-w-0 flex-1">
            <h2
              id={titleId}
              className="text-base font-semibold text-text-strong-950"
            >
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-1 text-sm text-text-sub-600">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-text-soft-400 transition-colors hover:bg-bg-weak-50 hover:text-text-strong-950"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Body */}
        {children && (
          <div className="flex-1 overflow-y-auto p-5">{children}</div>
        )}

        {/* Footer */}
        {footer && (
          <div className="flex justify-end gap-3 border-t border-stroke-soft-200 p-5">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Dialog;
